import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, Legend
} from 'recharts';
import {
  TrendingUp, Users, Target, MessageSquare, Heart, Repeat2,
  Hash, Zap, ArrowUpRight, ArrowDownRight, Minus, Loader2,
  Globe, Clock, Monitor, Smartphone, MousePointer, ExternalLink
} from 'lucide-react';

const COLORS = {
  cyan: '#00F0FF',
  orange: '#F59E0B',
  green: '#10B981',
  red: '#EF4444',
  gray: '#6B7280',
  purple: '#8B5CF6',
};

const CATEGORY_COLORS = {
  'Efficiency': COLORS.cyan,
  'Impact': COLORS.orange,
  'Sovereign': COLORS.green,
  'Unknown': COLORS.gray,
  'Noise': COLORS.red,
};

const ScoreTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-[#0a0a0f] border border-white/20 rounded-lg p-3 shadow-xl">
        <p className="text-[#F3F4F6] text-sm font-medium">{data.label}</p>
        <p className="text-xs text-[#6B7280]">Score: {data.range}</p>
        <p className="text-xs text-[#00F0FF]">{data.count} accounts</p>
      </div>
    );
  }
  return null;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0a0a0f] border border-white/20 rounded-lg p-3 shadow-xl">
        <p className="text-[#F3F4F6] text-sm font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-xs" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = COLORS.cyan }) => (
  <Card className="bg-white/[0.02] border-white/10">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[#6B7280] text-xs font-mono uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
          {subtitle && <p className="text-[#6B7280] text-xs mt-1">{subtitle}</p>}
        </div>
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}10` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
      {trend !== undefined && (
        <div className="flex items-center mt-3 text-xs">
          {trend > 0 ? (
            <><ArrowUpRight className="w-3 h-3 text-green-400 mr-1" /><span className="text-green-400">+{trend}%</span></>
          ) : trend < 0 ? (
            <><ArrowDownRight className="w-3 h-3 text-red-400 mr-1" /><span className="text-red-400">{trend}%</span></>
          ) : (
            <><Minus className="w-3 h-3 text-gray-400 mr-1" /><span className="text-gray-400">No change</span></>
          )}
          <span className="text-[#6B7280] ml-1">vs previous period</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Discovery Analytics Section
const DiscoveryAnalytics = ({ data }) => {
  if (!data || data.detail) return (
    <Card className="bg-white/[0.02] border-white/10">
      <CardContent className="py-12 text-center">
        <Target className="w-10 h-10 text-[#6B7280]/50 mx-auto mb-4" />
        <p className="text-[#F3F4F6] text-base">No discovery data available</p>
      </CardContent>
    </Card>
  );
  
  const { summary = {}, funnel = [], source_breakdown = [], timeline = [], top_seeds = [] } = data;
  
  // Filter out zero values from source breakdown
  const filteredSources = (source_breakdown || []).filter(s => s.count > 0);
  
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Discovered" 
          value={summary.total_found || 0} 
          subtitle={`${summary.total_runs || 0} discovery runs`}
          icon={Target}
          color={COLORS.cyan}
        />
        <StatCard 
          title="Auto-added" 
          value={summary.total_added || 0}
          subtitle={`${summary.add_rate || 0}% add rate`}
          icon={Users}
          color={COLORS.green}
        />
        <StatCard 
          title="Queued" 
          value={summary.total_queued || 0}
          subtitle="Awaiting review"
          icon={Zap}
          color={COLORS.orange}
        />
        <StatCard 
          title="Rejected" 
          value={summary.total_rejected || 0}
          subtitle="Below threshold"
          icon={Target}
          color={COLORS.red}
        />
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Source Breakdown Pie Chart */}
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">DISCOVERY SOURCES</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredSources.length > 0 ? (
              <div className="flex items-center">
                <ResponsiveContainer width="50%" height={180}>
                  <PieChart>
                    <Pie
                      data={filteredSources}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="count"
                    >
                      {filteredSources.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={Object.values(COLORS)[index % Object.values(COLORS).length]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {filteredSources.map((source, index) => (
                    <div key={source.source} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: Object.values(COLORS)[index % Object.values(COLORS).length] }}
                        />
                        <span className="text-[#F3F4F6]">{source.source}</span>
                      </div>
                      <span className="text-[#6B7280] font-mono">{source.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-[#6B7280] text-sm">
                No discovery data yet
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Discovery Timeline */}
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">DISCOVERY OVER TIME</CardTitle>
          </CardHeader>
          <CardContent>
            {timeline.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={timeline}>
                  <defs>
                    <linearGradient id="colorFound" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.cyan} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={COLORS.cyan} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(val) => val ? new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                    stroke="#6B7280"
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis stroke="#6B7280" tick={{ fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="found" 
                    stroke={COLORS.cyan} 
                    fillOpacity={1} 
                    fill="url(#colorFound)"
                    name="Found"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="added" 
                    stroke={COLORS.green} 
                    fillOpacity={0.3} 
                    fill={COLORS.green}
                    name="Added"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-[#6B7280] text-sm">
                No timeline data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Top Seeds */}
      {top_seeds.length > 0 && (
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">TOP PERFORMING SEEDS</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {top_seeds.slice(0, 10).map((seed, index) => (
                <div 
                  key={seed.seed} 
                  className="p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:border-[#00F0FF]/30 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#00F0FF]/20 text-[#00F0FF] text-xs">#{index + 1}</Badge>
                    <span className="text-[#F3F4F6] text-sm font-medium truncate">{seed.seed}</span>
                  </div>
                  <p className="text-[#6B7280] text-xs mt-1">{seed.discoveries} discoveries</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Watchlist Analytics Section
const WatchlistAnalytics = ({ data }) => {
  if (!data || data.detail) return (
    <Card className="bg-white/[0.02] border-white/10">
      <CardContent className="py-12 text-center">
        <Users className="w-10 h-10 text-[#6B7280]/50 mx-auto mb-4" />
        <p className="text-[#F3F4F6] text-base">No watchlist data available</p>
      </CardContent>
    </Card>
  );
  
  const { 
    summary = {}, 
    category_distribution = [], 
    score_distribution = [], 
    trending_tags = [], 
    growth_timeline = [] 
  } = data;
  
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Accounts" 
          value={summary.total_accounts || 0} 
          icon={Users}
          color={COLORS.cyan}
        />
        <StatCard 
          title="Avg Score" 
          value={summary.avg_score || 0}
          icon={Target}
          color={(summary.avg_score || 0) >= 70 ? COLORS.green : COLORS.orange}
        />
        <StatCard 
          title="Categories" 
          value={summary.categories || 0}
          icon={Zap}
          color={COLORS.purple}
        />
        <StatCard 
          title="Unique Tags" 
          value={summary.unique_tags || 0}
          icon={Hash}
          color={COLORS.orange}
        />
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">CATEGORY DISTRIBUTION</CardTitle>
          </CardHeader>
          <CardContent>
            {category_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={category_distribution} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#6B7280" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="category" type="category" stroke="#6B7280" tick={{ fontSize: 10 }} width={80} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Accounts" radius={[0, 4, 4, 0]}>
                    {category_distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[entry.category] || COLORS.gray} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-[#6B7280] text-sm">
                No category data
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Score Distribution */}
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">SCORE DISTRIBUTION</CardTitle>
          </CardHeader>
          <CardContent>
            {score_distribution.some(s => s.count > 0) ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={score_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="range" stroke="#6B7280" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#6B7280" tick={{ fontSize: 10 }} />
                  <Tooltip content={<ScoreTooltip />} />
                  <Bar dataKey="count" name="Accounts" fill={COLORS.cyan} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-[#6B7280] text-sm">
                No score data
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Trending Tags */}
      {trending_tags.length > 0 && (
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">TRENDING TAGS</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {trending_tags.map((tag, index) => (
                <Badge 
                  key={tag.tag}
                  className="px-3 py-1"
                  style={{
                    backgroundColor: `${COLORS.cyan}${Math.max(15, 30 - index * 2).toString(16)}`,
                    color: index < 5 ? COLORS.cyan : '#9CA3AF',
                    borderColor: index < 5 ? `${COLORS.cyan}40` : 'transparent',
                  }}
                >
                  #{tag.tag} <span className="ml-1 opacity-70">({tag.count})</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Growth Timeline */}
      {growth_timeline.length > 0 && (
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">WATCHLIST GROWTH</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={growth_timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  stroke="#6B7280"
                  tick={{ fontSize: 10 }}
                />
                <YAxis stroke="#6B7280" tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke={COLORS.cyan} 
                  strokeWidth={2}
                  dot={{ fill: COLORS.cyan, strokeWidth: 0 }}
                  name="Total"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Engagement Analytics Section
const EngagementAnalytics = ({ data }) => {
  if (!data) return null;
  
  const { summary, response_performance, response_type_performance, top_engagers, engagement_timeline } = data;
  
  if (summary.total_responses === 0) {
    return (
      <Card className="bg-white/[0.02] border-white/10">
        <CardContent className="py-12 text-center">
          <MessageSquare className="w-10 h-10 text-[#6B7280]/50 mx-auto mb-4" />
          <p className="text-[#F3F4F6] text-base">No engagement data yet</p>
          <p className="text-[#6B7280] text-sm mt-2">
            Post some responses to see engagement analytics
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard 
          title="Responses Posted" 
          value={summary.total_responses} 
          icon={MessageSquare}
          color={COLORS.cyan}
        />
        <StatCard 
          title="Total Likes" 
          value={summary.total_likes}
          icon={Heart}
          color={COLORS.red}
        />
        <StatCard 
          title="Retweets" 
          value={summary.total_retweets}
          icon={Repeat2}
          color={COLORS.green}
        />
        <StatCard 
          title="Replies" 
          value={summary.total_replies}
          icon={MessageSquare}
          color={COLORS.orange}
        />
        <StatCard 
          title="Avg Engagement" 
          value={summary.avg_engagement}
          icon={TrendingUp}
          color={COLORS.purple}
        />
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Response Type Performance */}
        {response_type_performance.length > 0 && (
          <Card className="bg-white/[0.02] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-mono text-[#F3F4F6]">RESPONSE TYPE PERFORMANCE</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={response_type_performance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="type" stroke="#6B7280" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#6B7280" tick={{ fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="avg_engagement" name="Avg Engagement" fill={COLORS.cyan} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
        
        {/* Top Engagers */}
        {top_engagers.length > 0 && (
          <Card className="bg-white/[0.02] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-mono text-[#F3F4F6]">TOP ENGAGERS</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {top_engagers.map((engager, index) => (
                  <div 
                    key={engager.handle}
                    className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-[#6B7280] text-xs font-mono w-5">#{index + 1}</span>
                      <span className="text-[#F3F4F6] text-sm">{engager.handle}</span>
                    </div>
                    <Badge className="bg-[#00F0FF]/20 text-[#00F0FF]">
                      {engager.interactions} interactions
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Engagement Timeline */}
      {engagement_timeline.length > 0 && (
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">ENGAGEMENT OVER TIME</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={engagement_timeline}>
                <defs>
                  <linearGradient id="colorLikes" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.red} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.red} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  stroke="#6B7280"
                  tick={{ fontSize: 10 }}
                />
                <YAxis stroke="#6B7280" tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area type="monotone" dataKey="likes" stroke={COLORS.red} fillOpacity={1} fill="url(#colorLikes)" name="Likes" />
                <Area type="monotone" dataKey="retweets" stroke={COLORS.green} fillOpacity={0.3} fill={COLORS.green} name="Retweets" />
                <Area type="monotone" dataKey="replies" stroke={COLORS.orange} fillOpacity={0.3} fill={COLORS.orange} name="Replies" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Pitches Analytics Section
const PitchesAnalytics = ({ data, getAuthHeaders }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [pitches, setPitches] = useState([]);
  const [selectedPitch, setSelectedPitch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  
  useEffect(() => {
    const fetchPitches = async () => {
      const headers = getAuthHeaders();
      try {
        const res = await fetch(`${API_URL}/api/pitches`, {
          headers,
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setPitches(data.pitches || []);
        }
      } catch (error) {
        console.error('Failed to fetch pitches:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPitches();
  }, [API_URL, getAuthHeaders]);
  
  const updateStatus = async (pitchId, status) => {
    setUpdating(pitchId);
    const headers = getAuthHeaders();
    try {
      const res = await fetch(`${API_URL}/api/pitches/${pitchId}/status?status=${status}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
      });
      if (res.ok) {
        setPitches(pitches.map(p => 
          p.pitch_id === pitchId ? { ...p, status, status_updated_at: new Date().toISOString() } : p
        ));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setUpdating(null);
    }
  };
  
  const downloadDeck = async (pitchId, companyName) => {
    const headers = getAuthHeaders();
    try {
      const res = await fetch(`${API_URL}/api/pitches/${pitchId}/deck`, {
        headers,
        credentials: 'include',
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${companyName.replace(/\s+/g, '_')}_pitch_deck.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Failed to download deck:', error);
    }
  };
  
  const STATUS_COLORS = {
    new: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
    reviewed: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
    interested: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
    passed: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' },
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }
  
  const stats = data || { total: 0, new: 0, reviewed: 0, interested: 0, passed: 0, this_week: 0, this_month: 0 };
  
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Pitches" 
          value={stats.total || 0} 
          icon={Target}
          color={COLORS.cyan}
        />
        <StatCard 
          title="New / Unreviewed" 
          value={stats.new || 0}
          icon={Zap}
          color={COLORS.orange}
        />
        <StatCard 
          title="This Week" 
          value={stats.this_week || 0}
          icon={TrendingUp}
          color={COLORS.green}
        />
        <StatCard 
          title="Interested" 
          value={stats.interested || 0}
          icon={Heart}
          color={COLORS.purple}
        />
      </div>
      
      {/* Stage & Category Charts */}
      {(stats.by_stage || stats.by_category) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {stats.by_stage && Object.keys(stats.by_stage).length > 0 && (
            <Card className="bg-white/[0.02] border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-mono text-[#F3F4F6]">BY STAGE</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(stats.by_stage).map(([stage, count], index) => (
                    <div key={stage} className="flex items-center justify-between">
                      <span className="text-[#F3F4F6] text-sm">{stage}</span>
                      <Badge className="bg-[#00F0FF]/20 text-[#00F0FF]">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {stats.by_category && Object.keys(stats.by_category).length > 0 && (
            <Card className="bg-white/[0.02] border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-mono text-[#F3F4F6]">BY CATEGORY</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(stats.by_category).map(([category, count], index) => (
                    <div key={category} className="flex items-center justify-between">
                      <span className="text-[#F3F4F6] text-sm">{category}</span>
                      <Badge className="bg-orange-500/20 text-orange-400">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      
      {/* Pitches List */}
      <Card className="bg-white/[0.02] border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono text-[#F3F4F6]">RECENT SUBMISSIONS</CardTitle>
        </CardHeader>
        <CardContent>
          {pitches.length === 0 ? (
            <div className="py-12 text-center">
              <Target className="w-10 h-10 text-[#6B7280]/50 mx-auto mb-3" />
              <p className="text-[#F3F4F6]">No pitches submitted yet</p>
              <p className="text-[#6B7280] text-sm mt-1">Pitches from founders will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pitches.map((pitch) => (
                <div 
                  key={pitch.pitch_id}
                  className="p-4 rounded-lg bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-[#F3F4F6] font-medium">{pitch.company?.name}</h4>
                        <Badge className={`${STATUS_COLORS[pitch.status]?.bg} ${STATUS_COLORS[pitch.status]?.text} text-xs`}>
                          {pitch.status}
                        </Badge>
                        <Badge variant="outline" className="text-[#6B7280] border-white/10 text-xs">
                          {pitch.company?.stage}
                        </Badge>
                      </div>
                      <p className="text-[#6B7280] text-sm mb-2">{pitch.company?.tagline}</p>
                      <div className="flex items-center gap-4 text-xs text-[#6B7280]">
                        <span>{pitch.founder?.full_name}</span>
                        <span>•</span>
                        <span>{pitch.fundraising?.amount_raising}</span>
                        <span>•</span>
                        <span>{formatDate(pitch.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => downloadDeck(pitch.pitch_id, pitch.company?.name)}
                        className="p-2 rounded bg-white/[0.02] border border-white/10 hover:border-[#00F0FF]/30 text-[#6B7280] hover:text-[#00F0FF] transition-colors"
                        title="Download Pitch Deck"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setSelectedPitch(selectedPitch === pitch.pitch_id ? null : pitch.pitch_id)}
                        className="p-2 rounded bg-white/[0.02] border border-white/10 hover:border-[#00F0FF]/30 text-[#6B7280] hover:text-[#00F0FF] transition-colors"
                        title="View Details"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {selectedPitch === pitch.pitch_id && (
                    <div className="mt-4 pt-4 border-t border-white/5 space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">Email</p>
                          <a href={`mailto:${pitch.founder?.email}`} className="text-[#00F0FF] text-sm hover:underline">
                            {pitch.founder?.email}
                          </a>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">LinkedIn</p>
                          <a href={pitch.founder?.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] text-sm hover:underline">
                            View Profile
                          </a>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">Website</p>
                          <a href={pitch.company?.website_url} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] text-sm hover:underline">
                            {pitch.company?.website_url}
                          </a>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">Category</p>
                          <p className="text-[#F3F4F6] text-sm">{pitch.company?.category}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">Previous Funding</p>
                          <p className="text-[#F3F4F6] text-sm">{pitch.fundraising?.previous_funding || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">Revenue</p>
                          <p className="text-[#F3F4F6] text-sm">{pitch.fundraising?.current_revenue || 'N/A'}</p>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">Description</p>
                        <p className="text-[#F3F4F6] text-sm">{pitch.company?.description}</p>
                      </div>
                      
                      <div>
                        <p className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">Why WyCo?</p>
                        <p className="text-[#F3F4F6] text-sm">{pitch.additional?.why_wyco}</p>
                      </div>
                      
                      <div className="flex items-center gap-2 pt-2">
                        <span className="text-[10px] text-[#6B7280] uppercase tracking-wider">Update Status:</span>
                        {['new', 'reviewed', 'interested', 'passed'].map((status) => (
                          <button
                            key={status}
                            onClick={() => updateStatus(pitch.pitch_id, status)}
                            disabled={updating === pitch.pitch_id || pitch.status === status}
                            className={`px-3 py-1 rounded text-xs transition-colors ${
                              pitch.status === status
                                ? `${STATUS_COLORS[status]?.bg} ${STATUS_COLORS[status]?.text} border ${STATUS_COLORS[status]?.border}`
                                : 'bg-white/5 text-[#6B7280] hover:bg-white/10'
                            }`}
                          >
                            {updating === pitch.pitch_id ? '...' : status}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Site Analytics Section
const SiteAnalytics = ({ data }) => {
  if (!data || data.detail) return (
    <Card className="bg-white/[0.02] border-white/10">
      <CardContent className="py-12 text-center">
        <Globe className="w-10 h-10 text-[#6B7280]/50 mx-auto mb-4" />
        <p className="text-[#F3F4F6] text-base">No site analytics data yet</p>
        <p className="text-[#6B7280] text-sm mt-2">
          Visit the landing page to start collecting data
        </p>
      </CardContent>
    </Card>
  );
  
  const { 
    summary = {}, 
    pageviews_timeline = [], 
    top_countries = [], 
    top_cities = [],
    top_referrers = [], 
    device_breakdown = [],
    browser_breakdown = [],
    pages = [],
    exit_pages = []
  } = data;
  
  // Colors for pie chart
  const DEVICE_COLORS = {
    'Desktop': COLORS.cyan,
    'Mobile': COLORS.orange,
    'Tablet': COLORS.green,
    'Unknown': COLORS.gray,
  };
  
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard 
          title="Page Views" 
          value={summary.total_pageviews || 0} 
          icon={MousePointer}
          color={COLORS.cyan}
        />
        <StatCard 
          title="Unique Visitors" 
          value={summary.unique_visitors || 0}
          icon={Users}
          color={COLORS.green}
        />
        <StatCard 
          title="Sessions" 
          value={summary.total_sessions || 0}
          icon={Globe}
          color={COLORS.purple}
        />
        <StatCard 
          title="Avg Time" 
          value={`${summary.avg_time_on_page || 0}s`}
          icon={Clock}
          color={COLORS.orange}
        />
        <StatCard 
          title="Avg Scroll" 
          value={`${summary.avg_scroll_depth || 0}%`}
          icon={TrendingUp}
          color={COLORS.cyan}
        />
        <StatCard 
          title="Bounce Rate" 
          value={`${summary.bounce_rate || 0}%`}
          icon={ExternalLink}
          color={summary.bounce_rate > 50 ? COLORS.red : COLORS.green}
        />
      </div>
      
      {/* Pageviews Timeline */}
      {pageviews_timeline.length > 0 && (
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">PAGEVIEWS OVER TIME</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={pageviews_timeline}>
                <defs>
                  <linearGradient id="colorPageviews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.cyan} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.cyan} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  stroke="#6B7280"
                  tick={{ fontSize: 10 }}
                />
                <YAxis stroke="#6B7280" tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="pageviews" 
                  stroke={COLORS.cyan} 
                  fillOpacity={1} 
                  fill="url(#colorPageviews)"
                  name="Pageviews"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      
      {/* Location & Device Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Countries */}
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">TOP COUNTRIES</CardTitle>
          </CardHeader>
          <CardContent>
            {top_countries.length > 0 ? (
              <div className="space-y-2">
                {top_countries.slice(0, 8).map((country, index) => (
                  <div key={country.country} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-[#6B7280] text-xs font-mono w-5">#{index + 1}</span>
                      <span className="text-[#F3F4F6] text-sm">{country.country}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-white/5 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#00F0FF] rounded-full"
                          style={{ width: `${country.percentage}%` }}
                        />
                      </div>
                      <span className="text-[#6B7280] text-xs font-mono w-12 text-right">
                        {country.visits}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[160px] flex items-center justify-center text-[#6B7280] text-sm">
                No location data yet
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Device Breakdown */}
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">DEVICES</CardTitle>
          </CardHeader>
          <CardContent>
            {device_breakdown.length > 0 ? (
              <div className="flex items-center">
                <ResponsiveContainer width="50%" height={160}>
                  <PieChart>
                    <Pie
                      data={device_breakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={35}
                      outerRadius={60}
                      paddingAngle={2}
                      dataKey="count"
                    >
                      {device_breakdown.map((entry) => (
                        <Cell 
                          key={entry.device} 
                          fill={DEVICE_COLORS[entry.device] || COLORS.gray} 
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {device_breakdown.map((device) => (
                    <div key={device.device} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        {device.device === 'Desktop' && <Monitor className="w-4 h-4 text-[#00F0FF]" />}
                        {device.device === 'Mobile' && <Smartphone className="w-4 h-4 text-[#F59E0B]" />}
                        {device.device === 'Tablet' && <Monitor className="w-4 h-4 text-[#10B981]" />}
                        <span className="text-[#F3F4F6]">{device.device}</span>
                      </div>
                      <span className="text-[#6B7280] font-mono">{device.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[160px] flex items-center justify-center text-[#6B7280] text-sm">
                No device data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Referrers & Browsers Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Referrers */}
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">TOP REFERRERS</CardTitle>
          </CardHeader>
          <CardContent>
            {top_referrers.length > 0 ? (
              <div className="space-y-2">
                {top_referrers.slice(0, 8).map((ref, index) => (
                  <div key={ref.referrer} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-[#6B7280] text-xs font-mono w-5">#{index + 1}</span>
                      <span className="text-[#F3F4F6] text-sm truncate max-w-[180px]">
                        {ref.referrer === 'direct' ? '(Direct)' : ref.referrer}
                      </span>
                    </div>
                    <Badge className="bg-[#00F0FF]/20 text-[#00F0FF]">{ref.visits}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[160px] flex items-center justify-center text-[#6B7280] text-sm">
                No referrer data yet
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Browsers */}
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-[#F3F4F6]">BROWSERS</CardTitle>
          </CardHeader>
          <CardContent>
            {browser_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={browser_breakdown.slice(0, 6)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#6B7280" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="browser" type="category" stroke="#6B7280" tick={{ fontSize: 10 }} width={70} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Visits" fill={COLORS.purple} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[160px] flex items-center justify-center text-[#6B7280] text-sm">
                No browser data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main Analytics Tab Component
const AnalyticsTab = () => {
  const { getAuthHeaders } = useAuth();
  const [timeRange, setTimeRange] = useState('all');
  const [loading, setLoading] = useState(true);
  const [discoveryData, setDiscoveryData] = useState(null);
  const [watchlistData, setWatchlistData] = useState(null);
  const [engagementData, setEngagementData] = useState(null);
  const [siteData, setSiteData] = useState(null);
  const [pitchesData, setPitchesData] = useState(null);
  const [activeSection, setActiveSection] = useState('site');
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      const daysParam = timeRange === 'all' ? '' : `?days=${timeRange === '7d' ? 7 : 30}`;
      const headers = getAuthHeaders();
      
      try {
        const [discovery, watchlist, engagement, site, pitches] = await Promise.all([
          fetch(`${API_URL}/api/analytics/discovery${daysParam}`, {
            headers,
            credentials: 'include',
          }).then(r => r.json()),
          fetch(`${API_URL}/api/analytics/watchlist${daysParam}`, {
            headers,
            credentials: 'include',
          }).then(r => r.json()),
          fetch(`${API_URL}/api/analytics/engagement${daysParam}`, {
            headers,
            credentials: 'include',
          }).then(r => r.json()),
          fetch(`${API_URL}/api/analytics/site${daysParam}`, {
            headers,
            credentials: 'include',
          }).then(r => r.json()),
          fetch(`${API_URL}/api/pitches/stats`, {
            headers,
            credentials: 'include',
          }).then(r => r.ok ? r.json() : null).catch(() => null),
        ]);
        
        setDiscoveryData(discovery);
        setWatchlistData(watchlist);
        setEngagementData(engagement);
        setSiteData(site);
        setPitchesData(pitches);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAnalytics();
  }, [timeRange, API_URL, getAuthHeaders]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header with Time Range Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setActiveSection('site')}
            className={`px-4 py-2 rounded-lg text-sm font-mono transition-colors ${
              activeSection === 'site' 
                ? 'bg-purple-500/10 text-purple-400 border border-purple-500/30' 
                : 'text-[#6B7280] hover:text-[#F3F4F6]'
            }`}
          >
            Site
          </button>
          <button
            onClick={() => setActiveSection('discovery')}
            className={`px-4 py-2 rounded-lg text-sm font-mono transition-colors ${
              activeSection === 'discovery' 
                ? 'bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30' 
                : 'text-[#6B7280] hover:text-[#F3F4F6]'
            }`}
          >
            Discovery
          </button>
          <button
            onClick={() => setActiveSection('watchlist')}
            className={`px-4 py-2 rounded-lg text-sm font-mono transition-colors ${
              activeSection === 'watchlist' 
                ? 'bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30' 
                : 'text-[#6B7280] hover:text-[#F3F4F6]'
            }`}
          >
            Watchlist
          </button>
          <button
            onClick={() => setActiveSection('engagement')}
            className={`px-4 py-2 rounded-lg text-sm font-mono transition-colors ${
              activeSection === 'engagement' 
                ? 'bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30' 
                : 'text-[#6B7280] hover:text-[#F3F4F6]'
            }`}
          >
            Engagement
          </button>
          <button
            onClick={() => setActiveSection('pitches')}
            className={`px-4 py-2 rounded-lg text-sm font-mono transition-colors ${
              activeSection === 'pitches' 
                ? 'bg-orange-500/10 text-orange-400 border border-orange-500/30' 
                : 'text-[#6B7280] hover:text-[#F3F4F6]'
            }`}
          >
            Pitches
            {pitchesData?.new > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-orange-500/20 text-orange-400 rounded-full">
                {pitchesData.new}
              </span>
            )}
          </button>
        </div>
        
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[140px] bg-white/[0.02] border-white/10 text-[#F3F4F6]">
            <SelectValue placeholder="Time range" />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0a0f] border-white/10">
            <SelectItem value="7d" className="text-[#F3F4F6]">Last 7 days</SelectItem>
            <SelectItem value="30d" className="text-[#F3F4F6]">Last 30 days</SelectItem>
            <SelectItem value="all" className="text-[#F3F4F6]">All time</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Content */}
      {activeSection === 'site' && <SiteAnalytics data={siteData} />}
      {activeSection === 'discovery' && <DiscoveryAnalytics data={discoveryData} />}
      {activeSection === 'watchlist' && <WatchlistAnalytics data={watchlistData} />}
      {activeSection === 'engagement' && <EngagementAnalytics data={engagementData} />}
      {activeSection === 'pitches' && <PitchesAnalytics data={pitchesData} getAuthHeaders={getAuthHeaders} />}
    </div>
  );
};

export default AnalyticsTab;
